"use client";

import Link from "@/lib/compat/link";
import { useTranslations, useLocale } from "@/lib/compat/i18n";
import { PillarRing } from "@/components/pillars/PillarRing";
import { Monogram } from "@/components/persona/Monogram";
import { CdmState } from "@/components/cdm/CdmState";
import { AgentBubble } from "@/components/agents/AgentBubble";
import { OrbIDCredential } from "@/components/identity/OrbIDCredential";
import { BankBadge } from "@/components/banks/BankBadge";
import { AttestorLogoSlot } from "@/components/attestors/AttestorLogoSlot";
import { isPublic } from "@/lib/profile";

/**
 * Component preview / smoke-test page. Demonstrates every core component
 * lights up correctly. Acts 0–6 will replace this in subsequent phases.
 *
 * Title flips between "Demo Day - Public" / "Demo Day - Private" via the
 * build-time DEMO_PROFILE flag so the two Fly deployments self-identify.
 */
export default function Home() {
  const t = useTranslations();
  const tLanding = useTranslations("landing");
  const locale = useLocale();
  const pdfPath = locale === "pt" ? "/presenter-remote-control-pt.pdf" : "/presenter-remote-control.pdf";
  const title = isPublic() ? "Demo Day - Public" : "Demo Day - Private";
  return (
    <main className="mx-auto max-w-5xl px-6 py-12 pe-[clamp(280px,28vw,420px)] text-orbitra-paper">
      <header className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-orbitra-teal-soft">Orbitra Global</p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-1 text-sm text-orbitra-muted">component preview</p>
        </div>
        <PillarRing fill={{ verify: 1, trace: 0.6, finance: 0.05 }} />
      </header>

      <section className="mt-8 flex items-center justify-between gap-4 rounded-md border border-state-financed/40 bg-state-financed/8 px-5 py-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-state-financed">{tLanding("presenterBadge")}</p>
          <p className="mt-0.5 font-display text-base font-semibold text-orbitra-paper">
            {tLanding("ctaHeading")}
          </p>
          <p className="mt-0.5 text-[11px] text-orbitra-muted">
            {tLanding("ctaBody")}
          </p>
        </div>
        <Link
          href="/rundemo"
          className="cta-primary px-5 py-2.5 font-display text-sm font-semibold"
        >
          {tLanding("ctaButton")}
        </Link>
      </section>

      <p className="mt-2 text-[11px] text-orbitra-muted">
        {tLanding("pdfLinkPrompt")}{" "}
        <a
          href={pdfPath}
          target="_blank"
          rel="noopener noreferrer"
          className="text-orbitra-teal-soft underline-offset-2 hover:text-state-verified hover:underline"
        >
          {tLanding("pdfLinkLabel")}
        </a>
      </p>

      <section className="mt-10 grid gap-6 md:grid-cols-3">
        <Tile label={t("pillars.verify")} tag={t("pillarTags.verify")} accent="state-verified" />
        <Tile label={t("pillars.trace")}  tag={t("pillarTags.trace")}  accent="state-traced" />
        <Tile label={t("pillars.finance")} tag={t("pillarTags.finance")} accent="state-financed" />
      </section>

      <section className="mt-10 grid gap-6 md:grid-cols-2">
        <div className="glass p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-teal-soft">Personas</p>
          <div className="mt-4 flex items-center gap-4">
            <div className="flex flex-col items-center gap-1">
              <Monogram name="Aurora Aves Brasil" flag="BR" accent="var(--state-verified)" />
              <span className="text-[11px] text-orbitra-muted">Aurora</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Monogram name="Al Madar Foods" flag="AE" accent="var(--state-cold)" />
              <span className="text-[11px] text-orbitra-muted">Al Madar</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Monogram name="Pampagro" flag="BR" accent="var(--state-verified)" />
              <span className="text-[11px] text-orbitra-muted">Pampagro</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Monogram name="Gulf Phosphate" flag="AE" accent="var(--state-verified)" />
              <span className="text-[11px] text-orbitra-muted">Gulf Phosphate</span>
            </div>
          </div>
        </div>

        <div className="glass p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-teal-soft">CDM state preview</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <CdmState token="DRAFT" />
            <CdmState token="EXTERNALLY_VALIDATED" />
            <CdmState token="SINGLE_WINDOW_SUBMITTED" />
            <CdmState token="MATCHED" />
            <CdmState token="INDETERMINATE" />
            <CdmState token="OVERRIDDEN" />
          </div>
        </div>
      </section>

      <section className="mt-10 grid gap-6 md:grid-cols-3">
        <BankBadge name="FAB" fundingPct={30} />
        <BankBadge name="Itau" fundingPct={50} />
        <div className="glass flex flex-col gap-3 px-5 py-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-teal-soft">Attestation partners</p>
          <AttestorRow
            partnerOrbId="orbid:cdial-halal-br"
            name="CDIAL Halal"
            legalName="Centro de Divulgação do Islam · BR"
            scope="Halal certification · BR"
          />
          <AttestorRow
            partnerOrbId="orbid:abcc-cert-br"
            name="ABCC"
            legalName="Arab-Brazilian Chamber of Commerce"
            scope="Origination attestation · BR ↔ AE"
          />
          <AttestorRow
            partnerOrbId="orbid:fed-uae-chambers"
            name="FUC"
            legalName="Federation of UAE Chambers"
            scope="Certificate of origin · UAE"
          />
        </div>
      </section>

      <section className="mt-10">
        <OrbIDCredential
          orbid="orbid:5493-almadar-ae"
          legalName="Al Madar Foods LLC"
          jurisdiction="UAE"
          lei="0000-DEMO-ALMADAR-AE"
          corpDid="did:orb:orbid:5493-almadar-ae"
          liveVerificationRef="lvc_a4f7c91d"
          kybAttestationRefs={["kyb_tradelicense_a1", "kyb_coi_a1", "kyb_ubo_a1", "kyb_aml_a1"]}
          verifiedTraderTier="Verified"
        />
      </section>

      <section className="mt-10 flex flex-col gap-3">
        <AgentBubble agent="echo"     line="Hi — I'll get Al Madar Foods LLC a Legal Entity Identifier in about 2 minutes." />
        <AgentBubble agent="vega"     line="I'm routing this to the Compliance Officer. Watch." />
        <AgentBubble agent="atlas"    line="I just anchored your Certificate of Origin to the Orbitra Global ledger — here's the timestamp." />
        <AgentBubble agent="mercator" line="LC placed. Settlement plan armed with triggerType: CustomsCleared." />
        <AgentBubble agent="sage"     line="This is a DocumentaryCreditComposite. Tenor-independent fee structure — passes the Tenor Independence Test. ✓" />
      </section>

      <footer className="mt-16 text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">
        {t("ui.confidential")} · component preview
      </footer>
    </main>
  );
}

function AttestorRow({
  partnerOrbId,
  name,
  legalName,
  scope,
}: {
  partnerOrbId: string;
  name: string;
  legalName: string;
  scope: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <AttestorLogoSlot partnerOrbId={partnerOrbId} fallbackName={name} size={28} />
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="text-[12px] font-semibold text-orbitra-paper">{name}</span>
        <span className="truncate text-[10px] text-orbitra-teal-soft">{legalName}</span>
        <span className="text-[9px] uppercase tracking-[0.16em] text-orbitra-muted">{scope}</span>
      </div>
    </div>
  );
}

function Tile({ label, tag, accent }: { label: string; tag: string; accent: "state-verified" | "state-traced" | "state-financed" }) {
  // Static class strings so Tailwind's content scanner picks them up.
  const border = {
    "state-verified": "border-state-verified",
    "state-traced":   "border-state-traced",
    "state-financed": "border-state-financed",
  }[accent];
  return (
    <div className={`glass border-t-2 px-5 py-4 ${border}`}>
      <p className="font-display text-base font-semibold">{label}</p>
      <p className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-orbitra-muted">{tag}</p>
    </div>
  );
}
